import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { AnnotationFlags, PDFArray, PDFDocument, PDFHexString, PDFName, PDFString, rgb } from 'pdf-lib';
import { getDocument } from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import Divider from 'shell/components/Divider';
import Dropdown from 'shell/components/Dropdown';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { normalize } from 'utils/helper';

function findTextMatchIndex(fullText, targetText) {
    const positionMap = [];
    const normalizedFull = normalize(
        fullText
            .split('')
            .filter((char, i) => {
                if (!/\s/.test(char)) {
                    positionMap.push(i);
                    return true;
                }
                return false;
            })
            .join('')
            .toLowerCase()
    );
    const normalizedTarget = normalize(targetText.replace(/\s+/g, '').toLowerCase());
    const matchIndex = normalizedFull.indexOf(normalizedTarget);
    if (matchIndex === -1) return null;
    const originalStart = positionMap[matchIndex];
    const originalEnd = positionMap[matchIndex + normalizedTarget.length - 1];
    return { start: originalStart, end: originalEnd + 1 };
}

async function findCoordinatesForTextMatch(pdf, pageNumber, targetText) {
    const page = await pdf.getPage(Number(pageNumber));
    const textContent = await page.getTextContent();
    const textItems = textContent.items;

    const fullText = textItems.map((t) => t.str).join('');
    const match = findTextMatchIndex(fullText, targetText);
    if (!match) return null;

    const { start: matchStart, end: matchEnd } = match;

    let charIndex = 0;
    const matchedItems = [];

    for (const item of textItems) {
        const itemStr = item.str;
        const itemStart = charIndex;
        const itemEnd = charIndex + itemStr.length;

        if (itemEnd > matchStart && itemStart < matchEnd) {
            matchedItems.push(item);
        }

        charIndex += itemStr.length;
    }

    if (matchedItems.length === 0) return null;

    return matchedItems.map((item) => {
        const [, , , fontHeight, x, y] = item.transform;
        return {
            x,
            y: y - Math.abs(fontHeight) * 0.3,
            width: item.width ?? 0,
            height: Math.abs(fontHeight) * 1.3,
        };
    });
}

const FileViewerControls = ({
    url,
    inlineComments,
    currentPageNum,
    totalNumPages,
    zoomPercent,
    isFullScreen,
    setIsFullScreen,
    onZoomIn,
    onZoomOut,
    isHighlightVisible,
    setIsHighlightVisible,
    onClickCreateComment,
}) => {
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const download = (pdfBytes, filename) => {
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(downloadUrl);
    };

    const handleSelect = async ({ key }) => {
        const fileArrayBuffer = await fetch(url).then((res) => res.arrayBuffer());
        let filename;
        try {
            filename = decodeURI(url.split('?').at(0).split('/').at(-1));
        } catch (e) {
            filename = 'submission.pdf';
        }

        if (key === 'download-original') {
            const pdfDoc = await PDFDocument.load(fileArrayBuffer);
            const pdfBytes = await pdfDoc.save();
            download(pdfBytes, filename);
        }

        if (key === 'download-with-comments') {
            const pdfDoc = await PDFDocument.load(fileArrayBuffer);
            const pdf = await getDocument({ data: fileArrayBuffer }).promise;

            for (const comment of inlineComments) {
                if (!comment.active || !comment.highlight) continue;
                const { highlight, content: contentText } = comment;
                let coords;
                try {
                    coords = await findCoordinatesForTextMatch(pdf, comment.page_num, highlight);
                } catch (e) {
                    console.error(e);
                    continue;
                }
                if (coords) {
                    const page = pdfDoc.getPage(Number(comment.page_num - 1));
                    const EPSILON = 0.5;
                    coords.forEach((item, index) => {
                        const previousItem = coords[index - 1];
                        const previousItemEnd = previousItem ? previousItem.x + previousItem.width : 0;
                        const sameLine = !previousItem || Math.abs(previousItem.y - item.y) < 2;
                        const overlap = sameLine && previousItemEnd - item.x > EPSILON ? previousItemEnd - item.x : 0;
                        const adjustedWidth = Math.max(0, item.width - overlap);

                        if (adjustedWidth > 0) {
                            page.drawRectangle({
                                x: item.x + overlap,
                                y: item.y,
                                width: adjustedWidth,
                                height: item.height,
                                color: rgb(0, 0.78, 0.33),
                                opacity: 0.25,
                            });
                        }
                    });
                    const firstCoords = coords[0];
                    const annotationRef = pdfDoc.context.nextRef();
                    const annotationDict = pdfDoc.context.obj({
                        Type: PDFName.of('Annot'),
                        Subtype: PDFName.of('Text'),
                        Rect: pdfDoc.context.obj([
                            firstCoords.x,
                            firstCoords.y + firstCoords.height,
                            firstCoords.x + 20,
                            firstCoords.y + 20,
                        ]),
                        Contents: PDFHexString.fromText(contentText),
                        Name: PDFName.of('Comment'),
                        Flags: pdfDoc.context.obj(AnnotationFlags.Print),
                        M: PDFString.fromDate(new Date()),
                    });
                    pdfDoc.context.assign(annotationRef, annotationDict);
                    const annots = page.node.lookup(PDFName.of('Annots'), PDFArray);
                    if (annots) {
                        annots.push(annotationRef);
                    } else {
                        page.node.set(PDFName.of('Annots'), pdfDoc.context.obj([annotationRef]));
                    }
                } else {
                    console.warn(`Could not find match for comment "${highlight}"`);
                }
            }
            const modifiedPdfBytes = await pdfDoc.save();
            download(modifiedPdfBytes, filename);
        }
    };

    return (
        <CustomStack device={device} direction={'row'} justifyContent={'space-between'}>
            <Stack
                alignItems={'center'}
                direction={'row'}
                divider={<CustomDivider flexItem orientation={'vertical'} />}
                spacing={2}
            >
                {currentPageNum && totalNumPages && (
                    <CustomText align={'center'} color={'inherit'} device={device}>
                        {device !== 'mobile' ? `${t('common:ui.page', 'Page')} ` : ''}
                        {currentPageNum}/{totalNumPages}
                    </CustomText>
                )}
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <CustomIconButton
                        aria-label={'zoom out'}
                        kind={'zoomOut'}
                        size={'small'}
                        tooltip={{
                            component: t('grading-section.document-preview.actions.zoom-out', 'Zoom Out'),
                            placement: 'bottom',
                        }}
                        variant={'square'}
                        onClick={onZoomOut}
                    />
                    {device !== 'mobile' && <Text color={'inherit'}>{zoomPercent}%</Text>}
                    <CustomIconButton
                        aria-label={'zoom in'}
                        kind={'zoomIn'}
                        size={'small'}
                        tooltip={{
                            component: t('grading-section.document-preview.actions.zoom-in', 'Zoom In'),
                            placement: 'bottom',
                        }}
                        variant={'square'}
                        onClick={onZoomIn}
                    />
                </Stack>
                <Stack direction={'row'} spacing={2}>
                    {onClickCreateComment && (
                        <CustomIconButton
                            aria-label={'add comment'}
                            kind={'newComment'}
                            size={'small'}
                            tooltip={{
                                component: t('grading-section.document-preview.actions.add-comment', 'Add Comment'),
                                placement: 'bottom',
                            }}
                            variant={'square'}
                            onClick={onClickCreateComment}
                        />
                    )}
                    {isHighlightVisible ? (
                        <CustomIconButton
                            aria-label={'show highlights'}
                            kind={'visible'}
                            size={'small'}
                            tooltip={{
                                component: t(
                                    'grading-section.document-preview.actions.show-highlights',
                                    'Show Highlights'
                                ),
                                placement: 'bottom',
                            }}
                            variant={'square'}
                            onClick={() => setIsHighlightVisible((current) => !current)}
                        />
                    ) : (
                        <CustomIconButton
                            aria-label={'hide highlights'}
                            kind={'hide'}
                            size={'small'}
                            tooltip={{
                                component: t(
                                    'grading-section.document-preview.actions.hide-highlights',
                                    'Hide Highlights'
                                ),
                                placement: 'bottom',
                            }}
                            variant={'square'}
                            onClick={() => setIsHighlightVisible((current) => !current)}
                        />
                    )}
                </Stack>
                {Boolean(url) && (
                    <Dropdown
                        id={'download-submission-dropdown'}
                        options={[
                            {
                                key: 'download-original',
                                value: t(
                                    'grading-section.document-preview.actions.download-original',
                                    'Download original'
                                ),
                            },
                            {
                                key: 'download-with-comments',
                                value: t(
                                    'grading-section.document-preview.actions.download-with-comments',
                                    'Download with comments'
                                ),
                            },
                        ]}
                        rootComponent={
                            <CustomIconButton
                                aria-label={'download submission'}
                                kind={'download'}
                                size={'small'}
                                tooltip={{
                                    component: t(
                                        'grading-section.document-preview.actions.download-as-pdf',
                                        'Download as PDF'
                                    ),
                                    placement: 'bottom',
                                }}
                                variant={'square'}
                            />
                        }
                        onSelect={handleSelect}
                    />
                )}
            </Stack>
            <CustomIconButton
                aria-label={'toggle fullscreen'}
                $active={!isFullScreen}
                kind={'sidebar'}
                size={'small'}
                variant={'square'}
                onClick={() => setIsFullScreen((current) => !current)}
            />
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ theme, device }) => {
    return {
        width: '100%',
        height: '32px',
        padding: device === 'mobile' ? theme.spacing(1, 3) : theme.spacing(1, 1, 1, 5),
        color: theme.palette.ensightful.shades[0],
        background: theme.palette.ensightful.neutral[700],
    };
});

const CustomText = styled(Text)(({ device }) => {
    return {
        width: device === 'mobile' ? '40px' : '78px',
    };
});

const CustomIconButton = styled(IconButton)(({ theme, $active }) => {
    return {
        color: theme.palette.ensightful.shades[0],
        ...($active && {
            background: theme.palette.ensightful.neutral[100],
            color: theme.palette.ensightful.shades[100],
        }),
    };
});

const CustomDivider = styled(Divider)(({ theme }) => {
    return {
        borderColor: theme.palette.ensightful.neutral[500],
    };
});

export default FileViewerControls;
